// 📄 pages/Proyectos/components/TareasSection.tsx
import { useEffect, useMemo, useState } from 'react';
import { Plus, RefreshCw, ClipboardList } from 'lucide-react';
import { useProject } from '../../../context/ProjectContext.tsx';
import { useAuth } from '../../../context/AuthContext.tsx';
import { getTareas, Tarea, ESTADOS_TAREA, TareaEstado } from '../../../services/tareaService.ts';
import { getHitos, Hito } from '../../../services/hitoService.ts';
import { getDisplayName } from '../../../utils/displayName.ts';
import TareaCard from './TareaCard.tsx';
import TareaFormModal from './TareaFormModal.tsx';
import TareaDetailModal from './TareaDetailModal.tsx';

type FiltroAsignacion = 'mias' | 'todas';

export default function TareasSection() {
  const { project, projectId, esCliente } = useProject();
  const { user, profile } = useAuth();
  const [tareas, setTareas] = useState<Tarea[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroAsignacion, setFiltroAsignacion] = useState<FiltroAsignacion>('mias');
  const [filtroEstado, setFiltroEstado] = useState<TareaEstado | 'todos'>('todos');
  const [formOpen, setFormOpen] = useState(false);
  const [editando, setEditando] = useState<Tarea | null>(null);
  const [seleccionada, setSeleccionada] = useState<Tarea | null>(null);
  const [hitos, setHitos] = useState<Hito[]>([]);
  const [filtroHito, setFiltroHito] = useState<string>('todos');

  const miembros = project?.miembros || [];

  const cargar = async () => {
    setLoading(true);
    try {
      const [data, listaHitos] = await Promise.all([getTareas(projectId), getHitos(projectId).catch(() => [] as Hito[])]);
      setTareas(data);
      setHitos(listaHitos);
      setSeleccionada((prev) => (prev ? data.find((t) => t.id === prev.id) || null : null));
    } catch (err) {
      console.error('Error cargando tareas:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const filtradas = useMemo(() => {
    return tareas.filter((t) => {
      if (filtroAsignacion === 'mias' && t.asignadoA !== user?.uid) return false;
      if (filtroEstado !== 'todos' && t.estado !== filtroEstado) return false;
      if (filtroHito !== 'todos' && (t.hitoId || '') !== filtroHito) return false;
      return true;
    });
  }, [tareas, filtroAsignacion, filtroEstado, filtroHito, user]);

  const handleSaved = () => {
    setFormOpen(false);
    setEditando(null);
    cargar();
  };

  if (!project || !user) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex gap-1.5">
            <button
              onClick={() => setFiltroAsignacion('mias')}
              className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                filtroAsignacion === 'mias' ? 'bg-blue-500/15 text-blue-300 border border-blue-500/30' : 'text-gray-400 border border-transparent hover:bg-white/5'
              }`}
            >
              Asignadas a mí
            </button>
            <button
              onClick={() => setFiltroAsignacion('todas')}
              className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                filtroAsignacion === 'todas' ? 'bg-blue-500/15 text-blue-300 border border-blue-500/30' : 'text-gray-400 border border-transparent hover:bg-white/5'
              }`}
            >
              Todas
            </button>
          </div>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value as TareaEstado | 'todos')}
            className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
          >
            <option value="todos">Todos los estados</option>
            {ESTADOS_TAREA.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
          {hitos.length > 0 && (
            <select
              value={filtroHito}
              onChange={(e) => setFiltroHito(e.target.value)}
              className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
            >
              <option value="todos">Todos los hitos</option>
              <option value="">Sin hito</option>
              {hitos.map((h) => <option key={h.id} value={h.id}>{h.nombre}</option>)}
            </select>
          )}
        </div>

        {!esCliente && (
          <button
            onClick={() => setFormOpen(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all"
          >
            <Plus className="w-4 h-4" /> Nueva Tarea
          </button>
        )}
      </div>

      {loading ? (
        <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
          <p className="text-sm">Cargando tareas...</p>
        </div>
      ) : filtradas.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <ClipboardList className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">
            {tareas.length === 0 ? 'Todavía no hay tareas en este proyecto.' : 'Ninguna tarea coincide con el filtro.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtradas.map((t) => (
            <TareaCard key={t.id} tarea={t} onClick={() => setSeleccionada(t)} />
          ))}
        </div>
      )}

      {seleccionada && (
        <TareaDetailModal
          projectId={projectId}
          tarea={seleccionada}
          esCliente={esCliente}
          onClose={() => setSeleccionada(null)}
          onUpdated={cargar}
          onEditar={() => { setEditando(seleccionada); setSeleccionada(null); }}
        />
      )}

      {(formOpen || editando) && (
        <TareaFormModal
          projectId={projectId}
          proyectoNombre={project.nombre}
          miembros={miembros}
          tarea={editando}
          creadoPor={user.uid}
          creadoPorNombre={getDisplayName(user, profile)}
          hitos={hitos}
          onClose={() => { setFormOpen(false); setEditando(null); }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
